// ============================================================================
// Vista: cambiar-plan — Panel Cliente
// Flujo INDEPENDIENTE de pago: el cliente solicita un cambio de plan.
// Usa solicitud_contratacion existente. SA aprueba/rechaza.
// ============================================================================

import { supabaseClient } from '@plataforma/supabase-client';
import { mostrarToast } from '@plataforma/ui';

import { eliminarComprobante, subirComprobante } from '../api/comprobantes.js';
import { obtenerEstadoSesion, recargarEmpresa } from '../estado-sesion.js';
import { exigirSesionEmpresa } from '../guardas.js';

import { montarShell } from './shell.js';

function moneda(valor) {
  return new Intl.NumberFormat('es-PE', {
    style: 'currency',
    currency: 'PEN',
    minimumFractionDigits: 2,
  }).format(Number(valor ?? 0));
}

// Estado del comprobante a nivel de módulo (sobrevive a limpieza del input en Android).
let archivoComprobanteCambioPlan = null;

export async function vistaCambiarPlan(contenedorRaiz) {
  if (!exigirSesionEmpresa()) return;

  const contenido = montarShell(contenedorRaiz, '/cambiar-plan');
  const empresa = obtenerEstadoSesion().empresa;
  const empresaId = empresa?.id;

  if (!empresaId) {
    contenido.innerHTML = `<p class="pf-campo__error">No se pudo cargar la información.</p>`;
    return;
  }

  const [planesRes, solicitudRes] = await Promise.all([
    supabaseClient()
      .from('plan')
      .select('id, nombre, precio_mensual')
      .eq('estado', 'activo')
      .order('precio_mensual', { ascending: true }),
    supabaseClient().rpc('rpc_obtener_solicitud_activa', { p_empresa_id: empresaId }),
  ]);

  const planes = (planesRes.data ?? []).filter((p) => p.id !== empresa?.plan?.id);

  // rpc_obtener_solicitud_activa es una función set-returning: supabase-js
  // expone el resultado como un ARRAY de filas (no un objeto). Se toma la
  // primera fila para obtener el objeto real de la solicitud activa.
  const solicitudActiva = solicitudRes.data?.[0] ?? null;
  const tieneSolicitudPendiente = solicitudActiva?.estado === 'pendiente';

  // Una solicitud "aprobada" solo representa un cambio REALMENTE programado
  // si la empresa conserva plan_pendiente_id. Si la aprobación ya se aplicó
  // (la migración 20260910 limpia plan_pendiente_id al activar el plan
  // pendiente), la fila de la solicitud queda "consumida"/histórica: el
  // cliente debe poder elegir un nuevo plan sin verse bloqueado.
  const tieneCambioProgramado =
    solicitudActiva?.estado === 'aprobada' &&
    empresa?.plan_pendiente_id !== null &&
    empresa?.plan_pendiente_id !== undefined;

  const planActual = empresa?.plan?.nombre ?? '—';
  const planProgramado = empresa?.plan_pendiente?.nombre ?? solicitudActiva?.plan_nombre ?? '—';

  contenido.innerHTML = `
    <div class="pf-page-header">
      <div>
        <h1 class="pf-page-header__titulo">Cambiar de plan</h1>
        <p class="pf-texto-suave">Solicita un cambio de plan. El equipo revisará tu solicitud.</p>
      </div>
    </div>

    <div class="pf-dashboard__seccion">
      <div class="pf-tarjeta" style="padding:16px;">
        <div class="pf-fila" style="gap:16px; flex-wrap:wrap; align-items:center;">
          <div>
            <span style="font-size:0.75rem; color:var(--pf-color-texto-terciario, #94a3b8); text-transform:uppercase;">Plan actual</span>
            <div style="font-size:1.1rem; font-weight:600;">${empresa?.plan?.nombre ?? '—'} — ${moneda(empresa?.plan?.precio_mensual ?? 0)}/mes</div>
          </div>
        </div>
      </div>
    </div>

    ${
      tieneSolicitudPendiente
        ? `
      <div class="pf-dashboard__seccion">
        <div class="pf-tarjeta" style="padding:24px; border-left:4px solid var(--pf-color-primario, #2563eb);">
          <h2 style="margin:0 0 8px; font-size:1rem; font-weight:600;">Solicitud en curso</h2>
          <p class="pf-texto-suave" style="margin:0;">
            Ya tienes una solicitud de cambio de plan en revisión: <strong>${solicitudActiva?.plan_nombre ?? '—'}</strong>.
            Estado: <pf-badge-estado tono="alerta">Pendiente</pf-badge-estado>
          </p>
        </div>
      </div>
    `
        : tieneCambioProgramado
          ? `
      <div class="pf-dashboard__seccion">
        <div class="pf-tarjeta" style="padding:24px; border-left:4px solid var(--pf-color-exito, #15803d);">
          <h2 style="margin:0 0 8px; font-size:1rem; font-weight:600;">Cambio de plan programado</h2>
          <p class="pf-texto-suave" style="margin:0;">
            Tu solicitud fue aprobada. El cambio se aplicará al vencimiento de tu período actual.
            Plan actual: <strong>${planActual}</strong> → Próximo plan: <strong>${planProgramado}</strong>.
          </p>
        </div>
      </div>
    `
          : `
      ${
        planes.length === 0
          ? `
        <div class="pf-dashboard__seccion">
          <div class="pf-tarjeta" style="padding:24px;">
            <p class="pf-texto-suave">No hay otros planes disponibles para cambiar.</p>
          </div>
        </div>
      `
          : `
        <div class="pf-dashboard__seccion">
          <div class="pf-tarjeta" style="padding:24px;">
            <h2 style="margin:0 0 16px; font-size:1rem; font-weight:600;">Selecciona el nuevo plan</h2>
            <form id="pf-form-cambio" style="display:flex;flex-direction:column;gap:12px;">
              <div class="pf-fila" style="gap:12px; flex-wrap:wrap;">
                ${planes
                  .map(
                    (p) => `
                  <label class="pf-tarjeta" style="flex:1; min-width:160px; padding:16px; cursor:pointer; border:2px solid var(--pf-color-borde, #e5e7eb); border-radius:12px; text-align:center; transition:border-color 0.15s;">
                    <input type="radio" name="pf-cambio-plan" value="${p.id}" style="display:none;" required />
                    <p style="margin:0; font-weight:700; font-size:1rem;">${p.nombre}</p>
                    <p style="margin:8px 0 0; font-size:1.25rem; font-weight:700; color:var(--pf-color-primario, #2563eb);">
                      ${moneda(p.precio_mensual)}<span style="font-size:0.75rem; font-weight:400;">/mes</span>
                    </p>
                  </label>`,
                  )
                  .join('')}
              </div>
              <div class="pf-campo">
                <label for="pf-cambio-comprobante">Comprobante de pago (imagen o PDF)</label>
                <input id="pf-cambio-comprobante" type="file" accept="image/*,.pdf" required />
              </div>
              <div class="pf-fila" style="justify-content:flex-end;gap:8px;">
                <button type="submit" class="pf-boton pf-boton--primario" id="pf-cambio-enviar">Enviar solicitud</button>
              </div>
            </form>
          </div>
        </div>
      `
      }
    `
    }
  `;

  // Highlight selected plan card
  contenido.querySelectorAll('input[name="pf-cambio-plan"]').forEach((radio) => {
    radio.addEventListener('change', () => {
      contenido.querySelectorAll('input[name="pf-cambio-plan"]').forEach((r) => {
        r.closest('.pf-tarjeta').style.borderColor = r.checked
          ? 'var(--pf-color-primario, #2563eb)'
          : 'var(--pf-color-borde, #e5e7eb)';
      });
    });
  });

  // Capturar comprobante a nivel de módulo (sobrevive a limpieza del input en Android).
  const inputCambioComprobante = contenido.querySelector('#pf-cambio-comprobante');
  const capturarCambio = () => {
    const file = inputCambioComprobante?.files?.[0] ?? null;
    if (file) archivoComprobanteCambioPlan = file;
  };
  inputCambioComprobante?.addEventListener('change', capturarCambio);
  inputCambioComprobante?.addEventListener('input', capturarCambio);

  // Handle submit
  const form = contenido.querySelector('#pf-form-cambio');
  if (form) {
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = contenido.querySelector('#pf-cambio-enviar');
      btn.disabled = true;
      btn.textContent = 'Enviando...';

      try {
        const inputCambio = contenido.querySelector('#pf-cambio-comprobante');
        const file = archivoComprobanteCambioPlan || inputCambio?.files?.[0];
        if (!file) {
          mostrarToast('Seleccione un comprobante.', { tono: 'alerta' });
          return;
        }

        const selectedPlan = contenido.querySelector('input[name="pf-cambio-plan"]:checked');
        if (!selectedPlan) {
          mostrarToast('Seleccione un plan.', { tono: 'alerta' });
          return;
        }

        // Subir comprobante via Edge Function (service_role) — el cliente nunca
        // escribe directo en Storage (bucket privado). Devuelve el PATH.
        const { ruta, error: uploadError } = await subirComprobante(file);
        if (uploadError || !ruta) {
          mostrarToast(
            `No se pudo subir el comprobante: ${uploadError?.message ?? 'intente nuevamente.'}`,
            { tono: 'peligro' },
          );
          return;
        }

        const { error: rpcError } = await supabaseClient().rpc('rpc_solicitar_cambio_plan', {
          p_empresa_id: empresaId,
          p_nuevo_plan_id: selectedPlan.value,
          p_comprobante_url: ruta,
          p_evidencia_mime: file.type || 'application/octet-stream',
          p_evidencia_nombre: file.name,
        });

        if (rpcError) {
          // Limpiar el objeto subido si falla la creación de la solicitud.
          await eliminarComprobante(ruta);
          throw rpcError;
        }

        mostrarToast(
          'Solicitud de cambio de plan enviada. El cambio se aplicará al vencimiento del período actual.',
          { tono: 'exito' },
        );
        await recargarEmpresa();
        await vistaCambiarPlan(contenedorRaiz);
      } catch (err) {
        mostrarToast(`Error: ${err.message}`, { tono: 'peligro' });
      } finally {
        btn.disabled = false;
        btn.textContent = 'Enviar solicitud';
      }
    });
  }
}
